import React, { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { maintenanceApi, ZitadelUnlinkedOrg } from "../../../api/maintenance";
import { useNotice } from "../shared/useNotice";
import {
  errText,
  useProjectRoles,
  RolePicker,
  UserCreatePanel,
} from "./zitadelShared";

/**
 * Flow 2 — org_custom already exists (no tenant id):
 *   pick org_custom → create Zitadel org → grant → users → link.
 */

const Done: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="ztl-summary" style={{ color: "#2e7d32" }}>
    <Check size={14} /> {children}
  </p>
);

const ZitadelFlowZitadelLink: React.FC = () => {
  const { roles } = useProjectRoles();
  const [phase, setPhase] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useNotice("error", "");

  const [orgs, setOrgs] = useState<ZitadelUnlinkedOrg[]>([]);
  const [ocName, setOcName] = useState("");
  const [platformUrl, setPlatformUrl] = useState("");
  const [zName, setZName] = useState("");
  const [zid, setZid] = useState("");
  const [grantRoles, setGrantRoles] = useState<string[]>([]);
  const [linkedTo, setLinkedTo] = useState("");

  useEffect(() => {
    maintenanceApi
      .zitadelUnlinkedOrgs()
      .then((r) => setOrgs(r.data))
      .catch((e) => setError(errText(e, "Failed to load org_custom rows")));
  }, []);
  useEffect(() => {
    if (roles.find((r) => r.key === "org-admin")) setGrantRoles(["org-admin"]);
  }, [roles]);

  const pick = () => {
    if (!ocName) return setError("Pick an org_custom row");
    setZName(ocName);
    setPlatformUrl(
      orgs.find((o) => o.name === ocName)?.platform_url || ""
    );
    setError("");
    setPhase(2);
  };

  const createZitadelOrg = async () => {
    if (!zName.trim()) return setError("Zitadel org name is required");
    setBusy(true);
    setError("");
    try {
      const r = await maintenanceApi.zitadelCreateOrg({ name: zName.trim() });
      setZid(r.data.org_id);
      setPhase(3);
    } catch (e) {
      setError(errText(e, "Failed to create Zitadel org"));
    } finally {
      setBusy(false);
    }
  };

  const doGrant = async () => {
    if (grantRoles.length === 0) return setError("Pick at least one role");
    setBusy(true);
    setError("");
    try {
      await maintenanceApi.zitadelCreateGrant(zid, { role_keys: grantRoles });
      setPhase(4);
    } catch (e) {
      setError(errText(e, "Failed to create project grant"));
    } finally {
      setBusy(false);
    }
  };

  const doLink = async () => {
    setBusy(true);
    setError("");
    try {
      const r = await maintenanceApi.zitadelLinkTenant({
        org_name: ocName,
        zitadel_org_id: zid,
      });
      setLinkedTo(r.data.zitadel_tenant_id);
      setPhase(6);
    } catch (e) {
      setError(errText(e, "Failed to link tenant"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ztl-card">
      {error && <p className="maint-form-error">{error}</p>}

      {phase > 1 && <Done>org_custom “{ocName}” selected</Done>}
      {phase > 2 && (
        <Done>
          Zitadel org created (<code>{zid}</code>)
        </Done>
      )}
      {phase > 3 && <Done>Project granted</Done>}

      {phase === 1 && (
        <>
          <h3 style={{ marginTop: 0 }}>1 · Pick org_custom (unlinked)</h3>
          <label className="ztl-field">
            <span>Organization *</span>
            <select
              value={ocName}
              onChange={(e) => setOcName(e.target.value)}
            >
              <option value="">Select org_custom...</option>
              {orgs.map((o) => (
                <option key={o.org_custom_id} value={o.name}>
                  {o.name}
                </option>
              ))}
            </select>
          </label>
          <div className="maint-form-actions">
            <button className="maint-btn-primary" onClick={pick}>
              Continue
            </button>
          </div>
        </>
      )}

      {phase === 2 && (
        <>
          <h3 style={{ marginTop: 0 }}>2 · Create Zitadel org</h3>
          <label className="ztl-field">
            <span>Zitadel org name *</span>
            <input
              value={zName}
              onChange={(e) => setZName(e.target.value)}
            />
          </label>
          <div className="maint-form-actions">
            <button
              className="maint-btn-primary"
              disabled={busy}
              onClick={createZitadelOrg}
            >
              {busy ? "Creating..." : "Create Zitadel org"}
            </button>
          </div>
        </>
      )}

      {phase === 3 && (
        <>
          <h3 style={{ marginTop: 0 }}>3 · Grant Admin Console project</h3>
          <RolePicker
            roles={roles}
            selected={grantRoles}
            onChange={setGrantRoles}
          />
          <div className="maint-form-actions" style={{ marginTop: 14 }}>
            <button
              className="maint-btn-primary"
              disabled={busy}
              onClick={doGrant}
            >
              {busy ? "Granting..." : "Grant project"}
            </button>
          </div>
        </>
      )}

      {phase === 4 && (
        <>
          <h3 style={{ marginTop: 0 }}>4 · Create dashboard user(s)</h3>
          <UserCreatePanel
            orgZitadelId={zid}
            roles={roles}
            loginUrl={platformUrl}
            orgName={ocName}
          />
          <div className="maint-form-actions" style={{ marginTop: 14 }}>
            <button
              className="maint-btn-primary"
              disabled={busy}
              onClick={doLink}
            >
              {busy ? "Linking..." : "Link & finish"}
            </button>
          </div>
        </>
      )}

      {phase === 6 && (
        <>
          <Done>
            Linked — {ocName}.zitadel_tenant_id = <code>{linkedTo}</code>
          </Done>
          <div className="maint-form-actions" style={{ marginTop: 12 }}>
            <button
              className="maint-btn-secondary"
              onClick={() => window.location.reload()}
            >
              Do another
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ZitadelFlowZitadelLink;
